import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { heroSlides, categories, hotRecommendations, latestUpdates, platformPartners, siteStats } from '@/mocks/homeData';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const heroImages: Record<number, string> = {
    1: 'https://readdy.ai/api/search-image?query=Epic%20Chinese%20action%20movie%20cinematic%20scene%20with%20martial%20arts%20master%20and%20horse%20in%20golden%20sunset%20light%2C%20dramatic%20atmosphere%2C%20warm%20amber%20tones%2C%20film%20poster%20quality%2C%20emotional%20powerful%20composition&width=1920&height=900&seq=home-hero-slide-1&orientation=landscape',
    2: 'https://readdy.ai/api/search-image?query=Elegant%20Chinese%20women%20performers%20on%20grand%20stage%20with%20dramatic%20spotlights%20and%20colorful%20visual%20effects%2C%20music%20show%20atmosphere%2C%20warm%20and%20vibrant%20tones%2C%20cinematic%20wide%20shot&width=1920&height=900&seq=home-hero-slide-2&orientation=landscape',
    3: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20Tang%20Dynasty%20city%20at%20night%20with%20lanterns%20and%20mysterious%20atmosphere%2C%20two%20detectives%20in%20traditional%20robes%2C%20suspenseful%20cinematic%20scene%2C%20warm%20golden%20lighting&width=1920&height=900&seq=home-hero-slide-3&orientation=landscape',
  };

  const recommendationImages: Record<string, string> = {
    'movie-wandering-earth-3': 'https://readdy.ai/api/search-image?query=Epic%20Chinese%20sci-fi%20space%20scene%20with%20massive%20engines%20and%20Earth%20in%20cosmic%20background%2C%20dramatic%20lighting%2C%20warm%20orange%20and%20deep%20blue%20contrast%2C%20cinematic%20movie%20poster%20style&width=600&height=350&seq=home-rec-wandering-earth-3&orientation=landscape',
    'tv-fanhua': 'https://readdy.ai/api/search-image?query=1990s%20Shanghai%20cityscape%20at%20night%20with%20neon%20lights%20and%20vintage%20atmosphere%2C%20nostalgic%20warm%20tones%2C%20film%20noir%20style%2C%20elegant%20Chinese%20drama%20aesthetic&width=600&height=350&seq=home-rec-fanhua&orientation=landscape',
    'variety-running-man': 'https://readdy.ai/api/search-image?query=Chinese%20outdoor%20variety%20show%20scene%20with%20energetic%20team%20running%20in%20colorful%20urban%20setting%2C%20bright%20sunny%20day%2C%20joyful%20atmosphere%2C%20dynamic%20composition&width=600&height=350&seq=home-rec-running-man&orientation=landscape',
    'anime-nezha-2': 'https://readdy.ai/api/search-image?query=Chinese%20anime%20style%20mythological%20hero%20with%20fire%20and%20wind%20effects%20in%20epic%20battle%20scene%2C%20vibrant%20red%20and%20gold%20colors%2C%20dynamic%20action%20pose%2C%20cinematic%20lighting&width=600&height=350&seq=home-rec-nezha-2&orientation=landscape',
    'tv-tang-dynasty': 'https://readdy.ai/api/search-image?query=Mysterious%20ancient%20Chinese%20palace%20corridor%20with%20candlelight%20and%20shadows%2C%20detective%20theme%2C%20Tang%20Dynasty%20costume%20drama%2C%20suspenseful%20warm%20amber%20lighting&width=600&height=350&seq=home-rec-tang-dynasty&orientation=landscape',
    'movie-pegasus-3': 'https://readdy.ai/api/search-image?query=Racing%20car%20speeding%20through%20desert%20rally%20track%20with%20dust%20and%20dramatic%20sunset%20sky%2C%20action%20movie%20cinematic%20shot%2C%20warm%20golden%20tones&width=600&height=350&seq=home-rec-pegasus-3&orientation=landscape',
    'variety-rap-china': 'https://readdy.ai/api/search-image?query=Chinese%20hip-hop%20stage%20performance%20with%20microphone%20and%20colorful%20spotlights%2C%20energetic%20music%20show%2C%20urban%20street%20culture%20aesthetic%2C%20vibrant%20warm%20lighting&width=600&height=350&seq=home-rec-rap-china&orientation=landscape',
    'anime-deep-sea-2': 'https://readdy.ai/api/search-image?query=Magical%20underwater%20world%20with%20colorful%20coral%20reefs%20and%20fantasy%20creatures%2C%20Chinese%20animation%20style%2C%20dreamy%20atmosphere%2C%20beautiful%20light%20rays%20through%20water&width=600&height=350&seq=home-rec-deep-sea-2&orientation=landscape',
  };

  const latestImages: Record<string, string> = {
    'movie-no-more-bets-2': 'https://readdy.ai/api/search-image?query=Suspenseful%20Chinese%20thriller%20scene%20with%20modern%20technology%20and%20mysterious%20atmosphere%2C%20anti-fraud%20theme%2C%20warm%20dramatic%20lighting%2C%20cinematic%20poster%20style&width=600&height=350&seq=home-latest-no-more-bets&orientation=landscape',
    'tv-minning-town-2': 'https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20rural%20landscape%20with%20green%20fields%20and%20modern%20village%20at%20sunset%2C%20warm%20pastoral%20atmosphere%2C%20hopeful%20and%20peaceful%20scene&width=600&height=350&seq=home-latest-minning-town&orientation=landscape',
    'variety-go-fighting-11': 'https://readdy.ai/api/search-image?query=Chinese%20reality%20TV%20show%20cast%20playing%20games%20in%20modern%20city%20street%2C%20fun%20and%20energetic%20atmosphere%2C%20bright%20daylight%2C%20colorful%20casual%20style&width=600&height=350&seq=home-latest-go-fighting&orientation=landscape',
    'anime-changan-2': 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20Tang%20Dynasty%20capital%20city%20with%20grand%20architecture%20and%20poetry%20atmosphere%2C%20anime%20art%20style%2C%20warm%20golden%20sunset%2C%20cultural%20elegance&width=600&height=350&seq=home-latest-changan&orientation=landscape',
    'movie-fengshen-2': 'https://readdy.ai/api/search-image?query=Epic%20Chinese%20mythological%20battle%20with%20warriors%20and%20mythical%20creatures%2C%20dramatic%20sky%20with%20fire%20and%20thunder%2C%20cinematic%20spectacle%2C%20warm%20and%20dark%20tones&width=600&height=350&seq=home-latest-fengshen-2&orientation=landscape',
    'tv-like-a-flowing-river-4': 'https://readdy.ai/api/search-image?query=Modern%20Chinese%20industrial%20cityscape%20at%20dawn%20with%20riverside%20view%2C%20reform%20era%20atmosphere%2C%20hopeful%20and%20ambitious%20mood%2C%20warm%20morning%20light&width=600&height=350&seq=home-latest-flowing-river&orientation=landscape',
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '糖豆视频',
    url: 'https://www.tangdoushipin.com',
    description: '糖豆视频免费在线观看平台，聚合西瓜视频免费、小蓝视频、茄子视频、芒果视频等站最新电影电视剧综艺资源',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.tangdoushipin.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '糖豆视频 - 首页',
    description: '糖豆视频免费在线观看平台首页，聚合最新电影电视剧综艺资源',
    url: 'https://www.tangdoushipin.com/',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <Navbar />

      <main>
        {/* Hero Banner */}
        <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={heroImages[slide.id] || heroImages[1]}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-8">
              <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl">
                  <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-4">
                    {heroSlides[currentSlide].tag}
                  </span>
                  <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight mb-4">
                    {heroSlides[currentSlide].title}
                  </h1>
                  <p className="text-white/80 text-base md:text-lg mb-6">
                    {heroSlides[currentSlide].subtitle}
                  </p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <Link
                      to={heroSlides[currentSlide].url}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-fill text-lg"></i>
                      立即观看
                    </Link>
                    <span className="text-white/60 text-sm">
                      {heroSlides[currentSlide].platform} 热播
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={prevSlide}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-colors cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-colors cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  index === currentSlide ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`幻灯片 ${index + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-8 py-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {siteStats.map((stat) => (
                  <div key={stat.label} className="flex items-center gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-foreground-950">{stat.value}</div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">视频分类</h2>
              <p className="text-foreground-500 text-sm">海量资源，一网打尽</p>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex flex-col items-center gap-3 p-5 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all group cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-500 transition-colors">
                    <i className={`${cat.icon} text-primary-600 group-hover:text-background-50 text-xl transition-colors`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">热门推荐</h2>
                <p className="text-foreground-500 text-sm">大家都在看的精彩内容</p>
              </div>
              <Link to="/category/movie" className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop="">
              {hotRecommendations.map((item) => (
                <VideoCard
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  category={item.category}
                  platform={item.platform}
                  rating={item.rating}
                  year={item.year}
                  updateInfo={item.updateInfo}
                  imageUrl={recommendationImages[item.imageSeq] || ''}
                  url={item.url}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">最新更新</h2>
                <p className="text-foreground-500 text-sm">实时同步各大平台最新内容</p>
              </div>
              <Link to="/category/tv" className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {latestUpdates.map((item) => (
                <Link key={item.id} to={item.url} className="group cursor-pointer">
                  <div className="aspect-[16/10] rounded-lg overflow-hidden mb-2.5 bg-background-200">
                    <img
                      src={latestImages[item.imageSeq] || ''}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-foreground-500">{item.category}</span>
                    <span className="text-xs text-foreground-400">·</span>
                    <span className="text-xs text-accent-600">{item.updateTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Partners */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">合作平台</h2>
              <p className="text-foreground-500 text-sm">聚合多平台优质视频资源</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {platformPartners.map((partner) => (
                <div key={partner.name} className="flex flex-col items-center gap-3 p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className={`${partner.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{partner.name}</span>
                  <span className="text-xs text-foreground-400">{partner.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-8 py-14 md:py-20 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20shapes%20in%20coral%20and%20amber%20tones%2C%20minimalist%20modern%20design%2C%20smooth%20texture%2C%20cinematic%20atmosphere&width=1920&height=500&seq=home-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              开始你的观影之旅
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-6 max-w-xl mx-auto">
              超过50,000部视频资源，覆盖电影、电视剧、综艺、动漫全品类，免费高清在线观看，无需等待即刻畅享。
            </p>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}